/**
 * Consumo de Firestore — panel exclusivo del superAdmin.
 *
 * Dos pestañas:
 *  1) Consumo del proyecto: métricas de Cloud Monitoring (lecturas, escrituras,
 *     peticiones API), serie diaria, perfil horario y tabla de los últimos días.
 *  2) Quién leyó: seguimiento de las lecturas que los clientes hicieron desde
 *     la app, agrupadas por usuario, por RUC y por pantalla.
 *
 * El control de acceso real está en el servidor (functions/metricasConsumo.js):
 * estas funciones verifican el rol `superAdmin` contra el documento del usuario
 * y responden `permission-denied` a cualquier otro. La comprobación del cliente
 * es solo para no renderizar la pantalla.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Form, Spinner, Table } from "react-bootstrap";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  PointElement,
  LinearScale,
  Tooltip as ChartTooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { FaChartLine, FaDatabase, FaUsers } from "react-icons/fa";
import firebaseApp from "../../firebase/credenciales";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  ChartTooltip,
  Legend
);

const functions = getFunctions(firebaseApp);

const nf = (n, d = 0) =>
  Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: d, maximumFractionDigits: d });

const hoyIso = () => {
  const d = new Date();
  return new Date(d.getTime() - 5 * 3600 * 1000).toISOString().slice(0, 10);
};

const KpiCard = ({ etiqueta, valor, pie, variante = "primary" }) => (
  <Card className="h-100 shadow-sm">
    <Card.Body className="py-3">
      <div
        className="text-uppercase"
        style={{ fontSize: "0.72rem", letterSpacing: "0.08em", opacity: 0.65 }}
      >
        {etiqueta}
      </div>
      <div className="fw-bold" style={{ fontSize: "1.75rem", lineHeight: 1.2 }}>
        {valor}
      </div>
      {pie ? (
        <div className="small" style={{ opacity: 0.7 }}>
          {pie}
        </div>
      ) : null}
    </Card.Body>
  </Card>
);

const ConsumoFirestore = ({ usuario }) => {
  const esSuperAdmin = String(usuario?.rol || "") === "superAdmin";

  const [pestana, setPestana] = useState("metricas");
  const [cargandoMetricas, setCargandoMetricas] = useState(false);
  const [cargandoSeguimiento, setCargandoSeguimiento] = useState(false);
  const [error, setError] = useState("");
  const [resumen, setResumen] = useState(null);
  const [seguimiento, setSeguimiento] = useState(null);
  const [fechaSeguimiento, setFechaSeguimiento] = useState(hoyIso());

  const cargarMetricas = useCallback(async () => {
    setCargandoMetricas(true);
    setError("");
    try {
      const callable = httpsCallable(functions, "obtenerMetricasFirestore");
      const { data } = await callable({ dias: 31 });
      if (data?.ok) setResumen(data.resumen);
    } catch (err) {
      setError(
        err?.code === "permission-denied"
          ? "Solo un superAdmin puede ver esta información."
          : err?.message || "No se pudieron cargar las métricas."
      );
    } finally {
      setCargandoMetricas(false);
    }
  }, []);

  const cargarSeguimiento = useCallback(async (fecha) => {
    setCargandoSeguimiento(true);
    setError("");
    try {
      const callable = httpsCallable(functions, "obtenerConsumoLecturas");
      const { data } = await callable({ fecha });
      if (data?.ok) setSeguimiento(data);
    } catch (err) {
      setError(
        err?.code === "permission-denied"
          ? "Solo un superAdmin puede ver esta información."
          : err?.message || "No se pudo cargar el seguimiento de lecturas."
      );
      setSeguimiento(null);
    } finally {
      setCargandoSeguimiento(false);
    }
  }, []);

  useEffect(() => {
    if (!esSuperAdmin) return;
    cargarMetricas();
    cargarSeguimiento(fechaSeguimiento);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esSuperAdmin]);

  const datosGraficos = useMemo(() => {
    if (!resumen) return null;
    const k = resumen.kpi;

    const ultimos = (resumen.diario?.lecturas || []).slice(-31);
    const dias = ultimos.map((x) => x.dia.slice(5));
    const lecturas = ultimos.map((x) => x.valor);
    const escriturasPorDia = new Map((resumen.diario?.escrituras || []).map((x) => [x.dia, x.valor]));
    const escrituras = ultimos.map((x) => escriturasPorDia.get(x.dia) || 0);

    const perfil = resumen.perfil?.lecturas || Array(24).fill(0);

    return {
      k,
      dias,
      lecturas,
      escrituras,
      perfil,
      diarioBar: {
        labels: dias,
        datasets: [
          {
            type: "bar",
            label: "Lecturas",
            data: lecturas,
            backgroundColor: "rgba(59,130,246,0.75)",
            borderRadius: 3,
            yAxisID: "y",
          },
          {
            type: "line",
            label: "Escrituras",
            data: escrituras,
            borderColor: "#f59e0b",
            backgroundColor: "rgba(245,158,11,0.15)",
            borderWidth: 2,
            pointRadius: 0,
            tension: 0.3,
            yAxisID: "y1",
          },
        ],
      },
      perfilBar: {
        labels: Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, "0")}:00`),
        datasets: [
          {
            label: "Lecturas por hora (promedio 7 días)",
            data: perfil,
            backgroundColor: "rgba(59,130,246,0.75)",
            borderRadius: 3,
          },
        ],
      },
    };
  }, [resumen]);

  if (!esSuperAdmin) {
    return (
      <Alert variant="danger" className="m-3">
        <FaUsers className="me-2" />
        Acceso restringido: esta pantalla es exclusiva del <strong>superAdmin</strong>.
      </Alert>
    );
  }

  const tablaDias = (() => {
    if (!resumen) return [];
    const L = (resumen.diario?.lecturas || []).slice(-10);
    const E = new Map((resumen.diario?.escrituras || []).map((x) => [x.dia, x.valor]));
    const P = new Map((resumen.diario?.peticiones || []).map((x) => [x.dia, x.valor]));
    const max = Math.max(...L.map((x) => x.valor), 1);
    return L.map((x) => ({
      dia: x.dia,
      lecturas: x.valor,
      escrituras: E.get(x.dia) || 0,
      peticiones: P.get(x.dia) || 0,
      porcentaje: (x.valor / max) * 100,
    }));
  })();

  return (
    <div className="container-fluid py-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h4 className="mb-0">
            <FaChartLine className="me-2" /> Consumo de Firestore
          </h4>
          <div className="small text-muted">
            Proyecto <code>tuboletaya</code> · Cloud Monitoring · solo superAdmin
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button size="sm" variant="outline-secondary" onClick={cargarMetricas} disabled={cargandoMetricas}>
            {cargandoMetricas ? "Actualizando..." : "Actualizar métricas"}
          </Button>
        </div>
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${pestana === "metricas" ? "active" : ""}`}
            onClick={() => setPestana("metricas")}
          >
            <FaDatabase className="me-1" /> Consumo del proyecto
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${pestana === "lecturas" ? "active" : ""}`}
            onClick={() => setPestana("lecturas")}
          >
            <FaUsers className="me-1" /> Quién leyó
          </button>
        </li>
      </ul>

      {pestana === "metricas" ? (
        <>
          {cargandoMetricas && !resumen ? (
            <div className="text-center py-5">
              <Spinner animation="border" /> <span className="ms-2">Consultando Cloud Monitoring...</span>
            </div>
          ) : null}

          {datosGraficos ? (
            <>
              <div className="row g-3 mb-3">
                <div className="col-12 col-md-6 col-xl-3">
                  <KpiCard
                    etiqueta="Lecturas hoy"
                    valor={nf(datosGraficos.k.lecturasHoy)}
                    pie={`${nf(datosGraficos.k.lecturasHoy / Math.max(datosGraficos.k.lecturasAyer, 1), 1)}× vs ayer`}
                  />
                </div>
                <div className="col-12 col-md-6 col-xl-3">
                  <KpiCard
                    etiqueta="Escrituras hoy"
                    valor={nf(datosGraficos.k.escriturasHoy)}
                    pie={`prom. 30 d ${nf(datosGraficos.k.escriturasProm30)}`}
                  />
                </div>
                <div className="col-12 col-md-6 col-xl-3">
                  <KpiCard
                    etiqueta="Peticiones API hoy"
                    valor={nf(datosGraficos.k.peticionesHoy)}
                    pie={`${nf(datosGraficos.k.docsPorPeticionHoy, 1)} docs/petición`}
                  />
                </div>
                <div className="col-12 col-md-6 col-xl-3">
                  <KpiCard
                    etiqueta="Lecturas por mes"
                    valor={nf(datosGraficos.k.lecturasProm30)}
                    pie="promedio 30 días"
                  />
                </div>
              </div>

              <Card className="shadow-sm mb-3">
                <Card.Body>
                  <Card.Title style={{ fontSize: "1rem" }}>Serie diaria · 31 días</Card.Title>
                  <div style={{ height: "320px" }}>
                    <Bar data={datosGraficos.diarioBar} options={{ responsive: true, maintainAspectRatio: false, interaction: { mode: "index", intersect: false }, scales: { y: { position: "left" }, y1: { position: "right", grid: { drawOnChartArea: false } } } }} />
                  </div>
                </Card.Body>
              </Card>

              <div className="row g-3 mb-3">
                <div className="col-12 col-lg-6">
                  <Card className="shadow-sm h-100">
                    <Card.Body>
                      <Card.Title style={{ fontSize: "1rem" }}>Perfil horario (hora Lima)</Card.Title>
                      <div style={{ height: "260px" }}>
                        <Bar data={datosGraficos.perfilBar} options={{ responsive: true, maintainAspectRatio: false, scales: { y: { beginAtZero: true } } }} />
                      </div>
                    </Card.Body>
                  </Card>
                </div>
                <div className="col-12 col-lg-6">
                  <Card className="shadow-sm h-100">
                    <Card.Body>
                      <Card.Title style={{ fontSize: "1rem" }}>Últimos 10 días</Card.Title>
                      <div style={{ maxHeight: "260px", overflowY: "auto" }}>
                        <Table size="sm" striped bordered hover className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Fecha</th>
                              <th className="text-end">Lecturas</th>
                              <th className="text-end">Escrituras</th>
                              <th className="text-end">Peticiones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tablaDias.map((f) => (
                              <tr key={f.dia}>
                                <td>
                                  {f.dia}
                                  <div
                                    className="progress mt-1"
                                    style={{ height: "3px", width: "70px" }}
                                  >
                                    <div
                                      className="progress-bar bg-primary"
                                      style={{ width: `${f.porcentaje}%` }}
                                    />
                                  </div>
                                </td>
                                <td className="text-end">{nf(f.lecturas)}</td>
                                <td className="text-end">{nf(f.escrituras)}</td>
                                <td className="text-end">{nf(f.peticiones)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </div>

              <Card className="shadow-sm">
                <Card.Body>
                  <Card.Title style={{ fontSize: "1rem" }}>Origen de las lecturas (hoy)</Card.Title>
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <div className="small text-muted mb-2">Tipo de lectura</div>
                      {Object.entries(datosGraficos.k.tiposHoy || {})
                        .sort((a, b) => b[1] - a[1])
                        .map(([tipo, valor]) => (
                          <div key={tipo} className="d-flex justify-content-between border-bottom py-1">
                            <span><code>{tipo}</code></span>
                            <span>
                              {nf(valor)}{" "}
                              <span className="text-muted">
                                ({nf((100 * valor) / Math.max(datosGraficos.k.lecturasHoy, 1), 1)}%)
                              </span>
                            </span>
                          </div>
                        ))}
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="small text-muted mb-2">Método de API</div>
                      {Object.entries(datosGraficos.k.metodosHoy || {})
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 6)
                        .map(([metodo, valor]) => (
                          <div key={metodo} className="d-flex justify-content-between border-bottom py-1">
                            <span><code>{metodo}</code></span>
                            <span>{nf(valor)}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </>
          ) : null}
        </>
      ) : null}

      {pestana === "lecturas" ? (
        <>
          <Card className="shadow-sm mb-3">
            <Card.Body className="d-flex flex-wrap justify-content-between align-items-end gap-3">
              <div>
                <Card.Title style={{ fontSize: "1rem" }} className="mb-1">
                  Seguimiento de lecturas de los clientes
                </Card.Title>
                <div className="small text-muted">
                  A partir del despliegue de la instrumentación. Los datos anteriores no son
                  recuperables: Cloud Monitoring no guarda el origen de las lecturas.
                </div>
              </div>
              <div className="d-flex align-items-end gap-2">
                <Form.Group controlId="fechaSeguimiento">
                  <Form.Label className="small mb-1">Fecha</Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={fechaSeguimiento}
                    max={hoyIso()}
                    onChange={(e) => setFechaSeguimiento(e.target.value)}
                  />
                </Form.Group>
                <Button
                  size="sm"
                  onClick={() => cargarSeguimiento(fechaSeguimiento)}
                  disabled={cargandoSeguimiento}
                >
                  Consultar
                </Button>
              </div>
            </Card.Body>
          </Card>

          {cargandoSeguimiento && !seguimiento ? (
            <div className="text-center py-5">
              <Spinner animation="border" /> <span className="ms-2">Consultando...</span>
            </div>
          ) : null}

          {seguimiento ? (
            <>
              <div className="row g-3 mb-3">
                <div className="col-12 col-md-4">
                  <KpiCard
                    etiqueta="Documentos leídos"
                    valor={nf(seguimiento.totalDocumentos)}
                    pie={`${nf(seguimiento.totalRegistros)} registros de actividad`}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <KpiCard
                    etiqueta="Usuarios activos"
                    valor={nf(seguimiento.usuarios?.length || 0)}
                    pie="usuarios que leyeron datos"
                  />
                </div>
                <div className="col-12 col-md-4">
                  <KpiCard
                    etiqueta="Empresas"
                    valor={nf(seguimiento.rucs?.length || 0)}
                    pie="RUCs con lecturas registradas"
                  />
                </div>
              </div>

              <Card className="shadow-sm mb-3">
                <Card.Body>
                  <Card.Title style={{ fontSize: "1rem" }}>Por usuario</Card.Title>
                  {!seguimiento.usuarios?.length ? (
                    <div className="text-muted small py-3">
                      Sin lecturas registradas para esta fecha.
                    </div>
                  ) : (
                    <div style={{ maxHeight: "420px", overflowY: "auto" }}>
                      <Table size="sm" striped bordered hover className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Usuario</th>
                            <th>Rol</th>
                            <th>RUC</th>
                            <th className="text-end">Documentos</th>
                            <th>Colecciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {seguimiento.usuarios.map((u) => (
                            <tr key={u.email || u.dni}>
                              <td>
                                <div className="fw-semibold">{u.nombre || u.email || "—"}</div>
                                <div className="small text-muted">{u.email || u.dni || ""}</div>
                              </td>
                              <td>
                                {u.rol ? <Badge bg="secondary">{u.rol}</Badge> : "—"}
                              </td>
                              <td><code>{u.rucEmpresa || "—"}</code></td>
                              <td className="text-end fw-semibold">{nf(u.documentos)}</td>
                              <td className="small">
                                {Object.entries(u.colecciones || {})
                                  .sort((a, b) => b[1] - a[1])
                                  .map(([col, n]) => (
                                    <span key={col} className="badge bg-light text-dark me-1 border">
                                      {col}: {nf(n)}
                                    </span>
                                  ))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>

              <div className="row g-3">
                <div className="col-12 col-lg-6">
                  <Card className="shadow-sm h-100">
                    <Card.Body>
                      <Card.Title style={{ fontSize: "1rem" }}>Por empresa (RUC)</Card.Title>
                      <Table size="sm" striped bordered hover className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>RUC</th>
                            <th className="text-end">Documentos leídos</th>
                            <th className="text-end">%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(seguimiento.rucs || []).map((r) => (
                            <tr key={r.ruc}>
                              <td><code>{r.ruc}</code></td>
                              <td className="text-end fw-semibold">{nf(r.documentos)}</td>
                              <td className="text-end">
                                {nf(
                                  (100 * r.documentos) /
                                    Math.max(seguimiento.totalDocumentos, 1),
                                  1
                                )}
                                %
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </div>
                <div className="col-12 col-lg-6">
                  <Card className="shadow-sm h-100">
                    <Card.Body>
                      <Card.Title style={{ fontSize: "1rem" }}>Por pantalla</Card.Title>
                      <Table size="sm" striped bordered hover className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Origen</th>
                            <th className="text-end">Documentos leídos</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(seguimiento.origenes || []).map((o) => (
                            <tr key={o.origen}>
                              <td><code>{o.origen}</code></td>
                              <td className="text-end fw-semibold">{nf(o.documentos)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </div>
              </div>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default ConsumoFirestore;
